import { useState, useEffect } from 'react';
import axios from 'axios';
import { endpoints } from '../../../api/api';
import { Section, BookMetadata } from '../types';
import { trackBookLoadFailed } from '../../../utils/analytics';

export const DEFAULT_FRONT_MATTER: { id: string; title: string }[] = [
  { id: 'dedication', title: 'Dedication' },
  { id: 'foreword', title: 'Foreword' },
  { id: 'preface', title: 'Preface' },
  { id: 'acknowledgments', title: 'Acknowledgments' },
  { id: 'prologue', title: 'Prologue' },
];

export const useBookLoader = (bookId: string | null) => {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string>('dedication');
  const [isLoading, setIsLoading] = useState(!!bookId);
  const [loadFailed, setLoadFailed] = useState(false);
  const [bookTitle, setBookTitle] = useState<string>('Book Structure');
  const [bookSize, setBookSize] = useState<string>('5.5x8.5');
  const [bookLanguage, setBookLanguage] = useState<string>('English');
  const [bookMetadata, setBookMetadata] = useState<BookMetadata>({});

  useEffect(() => {
    const loadBookContent = async () => {
      if (!bookId) return;

      setIsLoading(true);
      try {
        const response = await axios.get(endpoints.getBook.replace(':id', bookId));
        const book = response.data.book;

        if (book.title) setBookTitle(book.title);
        if (book.bookSize) setBookSize(book.bookSize);
        if (book.language) setBookLanguage(book.language);

        setBookMetadata({
          author: book.author || '',
          subtitle: book.subtitle || '',
          year: book.publishedYear || new Date().getFullYear(),
          isbn: book.isbn || '',
          editor: book.editor || '',
          typesetter: book.typesetter || '',
          coverDesigner: book.coverDesigner || '',
          publishingConsultant: book.publishingConsultant || '',
          contributors: book.contributors || [],
        });

        if (book.content?.json?.sections && book.content.json.sections.length > 0) {
          setSections(book.content.json.sections);
          setActiveSectionId(book.content.json.sections[0].id);
        }
      } catch (error: any) {
        console.error('Failed to load book content:', error);
        trackBookLoadFailed(error?.response?.data?.message || error?.message || 'unknown');
        setLoadFailed(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadBookContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId]);

  return {
    sections,
    setSections,
    activeSectionId,
    setActiveSectionId,
    isLoading,
    loadFailed,
    bookTitle,
    bookSize,
    bookLanguage,
    bookMetadata
  };
};
